import {
  Calendar,
  CheckSquare,
  Images,
  MessageCircle,
  Users,
  type LucideIcon,
  LayoutDashboard,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import {
  BrandLogo,
  BrandLogoSize,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  VersionBadge,
} from '@/shared/components/ui';
import { buildInfo } from '@/shared/config/build';
import { ROUTE_PATHS, RouteKey } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { SheetSide } from '../ui/sheet';

interface NavigationItem {
  readonly name: string;
  readonly route: RouteKey;
  readonly icon: LucideIcon;
}

const navigation: readonly NavigationItem[] = [
  { name: 'Начало', route: RouteKey.DASHBOARD, icon: LayoutDashboard },
  { name: 'Календар', route: RouteKey.FAMILY_CALENDAR, icon: Calendar },
  { name: 'Задачи', route: RouteKey.TODO_LIST, icon: CheckSquare },
  { name: 'Албуми', route: RouteKey.ALBUMS, icon: Images },
  { name: 'Чат', route: RouteKey.P2P_CHAT, icon: MessageCircle },
  { name: 'Семейство', route: RouteKey.FAMILY_SETTINGS, icon: Users },
] as const;

interface MobileSidebarProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function MobileSidebar({ open, onClose }: MobileSidebarProps) {
  const location = useLocation();

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side={SheetSide.Left} className="w-[280px] p-0">
        <SheetHeader className="border-b border-border p-4">
          <SheetTitle className="flex items-center gap-2 text-left">
            <BrandLogo size={BrandLogoSize.Small} showText />
          </SheetTitle>
        </SheetHeader>

        <nav className="flex h-[calc(100vh-5rem)] flex-col">
          <div className="flex flex-col gap-1 p-4">
            {navigation.map((item) => {
              const href = ROUTE_PATHS[item.route];
              const isActive = location.pathname.startsWith(href);
              return (
                <Link
                  key={item.name}
                  to={href}
                  onClick={onClose}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors',
                    'min-h-[44px]',
                    isActive
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                  )}
                >
                  <item.icon className="size-5" aria-hidden="true" />
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Version badge at the bottom */}
          <div className="mt-auto border-t border-border p-4">
            <VersionBadge version={buildInfo.version} />
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
